import React, { useState, useEffect } from "react";
import SEO from "../seo";
const SetupShippingAndTax = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = ["overview", "configure-shipping", "configure-tax"];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Set Up Shipping and Tax Rules – xPAGE Help Center"
        description="Learn how to configure shipping zones, rates, and tax rules in your xPage store to ensure accurate costs and compliance with local regulations."
        keywords="xPAGE, shipping, tax rules, shipping zones, tax rates, online store setup, eCommerce compliance"
        canonical="https://help.xpage.ai/help/landing-page-compliance/Set%20Up%20Shipping%20and%20Tax%20Rules"
        type="article"
      />
      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Main Title */}
        <h1 className="text-systemTitle text-2xl font-semibold mb-3">
          Set Up Shipping and Tax Rules
        </h1>

        {/* Introduction Paragraph */}
        <p className="text-sm text-systemLabel mb-6">
          Before launching your landing page store, it's essential to configure{" "}
          <strong>shipping zones, rates, and tax rules</strong>. This ensures
          customers see accurate delivery costs and taxes at checkout, while
          keeping your store compliant with local regulations.
        </p>

        {/* Overview Section */}
        <section id="overview" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Overview
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-3">
              With xPage, you can:
            </p>

            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li>
                Define <strong>shipping zones</strong> by country or region.
              </li>
              <li>
                Set <strong>flat rates, free shipping</strong>, or{" "}
                <strong>custom calculated rates</strong>.
              </li>
              <li>
                Create <strong>different shipping profiles</strong> for
                different product groups.
              </li>
              <li>
                Enable <strong>automatic tax calculations</strong> or configure{" "}
                <strong>manual tax rates</strong>.
              </li>
            </ul>
          </div>
        </section>

        {/* Configure Shipping Rules Section */}
        <section id="configure-shipping" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Configure Shipping Rules
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              Steps:
            </h3>

            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li className="mb-2">
                <span className="font-medium">
                  Log in to the <strong>xPage Admin</strong>
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Navigate to <strong>Settings</strong> →{" "}
                  <strong>Shipping and Delivery</strong>
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Click <strong>Create Shipping</strong>
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Select the <strong>country or region</strong> for delivery
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Configure your shipping methods:
                </span>
                <ul className="list-disc list-inside ml-6 mt-1 space-y-1 text-system600">
                  <li>
                    <strong>Flat Rate:</strong> One fixed fee for all orders in
                    the zone.
                  </li>
                  <li>
                    <strong>Free Shipping:</strong> Available for all orders or
                    above a set amount.
                  </li>
                  <li>
                    <strong>Custom Rate:</strong> Based on weight, price, or
                    product type.
                  </li>
                </ul>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Modify conditions for each rate (e.g.,{" "}
                  <strong>minimum/maximum price, weight limits</strong>)
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Click <strong>Save</strong>
                </span>
              </li>
            </ol>

            {/* Tip Box */}
            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 mt-3">
              <p className="text-sm text-systemLabel">
                <span className="font-semibold">Tip:</span> You can create
                multiple shipping zones to offer different rates for different
                regions.
              </p>
            </div>
          </div>
        </section>

        {/* Configure Tax Rules Section */}
        <section id="configure-tax" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Configure Tax Rules
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              Steps:
            </h3>

            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li className="mb-2">
                <span className="font-medium">
                  Go to <strong>Settings</strong> → <strong>Taxes</strong>
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">Choose between:</span>
                <ul className="list-disc list-inside ml-6 mt-1 space-y-1 text-system600">
                  <li>
                    <strong>Automatic Tax Calculation:</strong> Let xPage handle
                    tax rates based on customer location.
                  </li>
                  <li>
                    <strong>Manual Tax Rates:</strong> Set fixed rates for
                    specific regions.
                  </li>
                </ul>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  If manual, specify{" "}
                  <strong>country, state, and tax rate</strong>
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Enable or disable tax-inclusive pricing
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Click <strong>Save</strong>
                </span>
              </li>
            </ol>

            {/* Tip Box */}
            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 mt-3">
              <p className="text-sm text-systemLabel">
                <span className="font-semibold">Note:</span> Tax rules vary by
                region. Ensure your settings comply with local laws where you
                sell.
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar - Hidden on mobile, visible on desktop */}
      <div className="hidden lg:block lg:w-64 flex-shrink-0">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Overview
              </a>
            </li>
            <li>
              <a
                href="#configure-shipping"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "configure-shipping"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Configure Shipping
              </a>
            </li>
            <li>
              <a
                href="#configure-tax"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "configure-tax"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Configure Tax
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SetupShippingAndTax;
